import React, { useMemo, useState } from "react";
import {
  Controller,
  SubmitHandler,
  useFieldArray,
  useForm,
  useWatch,
} from "react-hook-form";
import Select from "react-select";
import moment from "moment";
import { LuAlertCircle, LuBuilding2, LuDatabase } from "react-icons/lu";
import { toast } from "react-toastify";

import TurningItemPickerModal, {
  TurningCardProps,
  TurningCardSize,
  turningCap,
} from "./TurningItemPickerModal";
import TurningOrderLines, { TurningLineView } from "./TurningOrderLines";
import {
  CuttingCategoryOption,
  categoryText,
  useCategoryBoxes,
} from "../cuttingLines/CuttingCategoryBoxes";
import Button from "../ui/Button";
import Input from "../ui/Input";
import cx from "../ui/cx";
import {
  buttonSpinnerClass,
  cardContentClass,
  fieldErrorIconClass,
  fieldErrorTextClass,
  fieldInputClass,
  fieldLabelClass,
  formCardClass,
  formSelectIconClass,
  gradientSubmitButtonClass,
} from "../ui/styles";
import { useAppSelector } from "../../app/hooks";
import { getSizeUnit } from "../../features/appSettings/appSettingsSlice";
import {
  cuttingLineKey,
  formatLength,
  optionKey,
  rateSourceLabel,
} from "../../static/cuttingLine";
import { formatAmount, formatQty } from "../../static/materialLine";
import { rateNumber, rateText } from "../../static/processRate";
import { iconSelectStyles, toastStyles } from "../../static/styles";
import { CuttingCategoryProps } from "../../types/cuttingOrder";
import { PartyOptionProps } from "../../types/materialLine";
import {
  ITurningLineInput,
  ITurningOrderFormInput,
  TurningOptionProps,
  TurningOptionSizeProps,
  TurningOrderItemProps,
  TurningOrderProps,
  TurningPartyInfoProps,
} from "../../types/turningOrder";

type Props = {
  defaultValues?: ITurningOrderFormInput;
  partyOptions: PartyOptionProps[];
  partyInfo: TurningPartyInfoProps;
  partyInfoLoading: boolean;
  // The order being edited, with what each of its lines copied and has received.
  savedOrder?: TurningOrderProps;
  // An order with receives keeps its party.
  partyLocked?: boolean;
  // Called when another party is picked, so the page can load that party's items.
  onPartyChange: (partyId: number) => void;
  onSubmit: (data: ITurningOrderFormInput) => void;
  isSubmitting: boolean;
  onCancel: () => void;
  submitLabel: string;
  // Rates only for a user who may see them.
  withRates: boolean;
};

type OptionEntry = { option: TurningOptionProps; size: TurningOptionSizeProps };

// "96mm", or "-" for a cut length the item page doesn't have (turning needs none).
const lengthText = (value: number | null | undefined, unit: string) =>
  value !== null && value !== undefined ? formatLength(value, unit) : "-";

// The form of a turning order, laid out like the cutting order form without its material: the party and the cut pieces
// to turn, picked from the picker by category. A line can't take more pieces than are cutting ready. A saved line keeps
// the material, cut length and rate it was added with; the server copies them for new lines.
const TurningOrderForm = ({
  defaultValues,
  partyOptions,
  partyInfo,
  partyInfoLoading,
  savedOrder,
  partyLocked,
  onPartyChange,
  onSubmit,
  isSubmitting,
  onCancel,
  submitLabel,
  withRates,
}: Props) => {
  const sizeUnit = useAppSelector(getSizeUnit);

  const {
    control,
    register,
    handleSubmit,
    setValue,
    getValues,
    clearErrors,
    formState: { errors },
  } = useForm<ITurningOrderFormInput>({
    defaultValues: defaultValues || {
      party_id: 0,
      date: moment().format("YYYY-MM-DDTHH:mm"),
      items: [],
    },
  });

  const { fields, remove, replace } = useFieldArray({ control, name: "items" });

  const partyId = useWatch({ control, name: "party_id" });
  const watchedItems = useWatch({ control, name: "items" });
  const items = useMemo(() => watchedItems || [], [watchedItems]);

  // The category whose items the picker shows, while it is open.
  const [pickerCategoryId, setPickerCategoryId] = useState<number | null>(
    null
  );
  const [showBoxErrors, setShowBoxErrors] = useState(false);

  // Each saved line's category, even once the party changed (the item keeps its category).
  const savedCategoryOfItem = useMemo(
    () =>
      new Map(
        (savedOrder?.items || []).map((item) => [
          item.item_id,
          item.item?.category_id ?? 0,
        ])
      ),
    [savedOrder]
  );

  const optionByKey = useMemo(() => {
    const entries = new Map<string, OptionEntry>();

    partyInfo.options.forEach((option) =>
      option.sizes.forEach((size) =>
        entries.set(optionKey(option.item_id, size.size_id), { option, size })
      )
    );

    return entries;
  }, [partyInfo]);

  // The order's own lines count only while it keeps its party: another party copies every line again.
  const partyChanged = !!savedOrder && Number(partyId) !== savedOrder.party_id;

  const savedByKey = useMemo(() => {
    const lines = new Map<string, TurningOrderItemProps>();

    if (savedOrder && !partyChanged) {
      savedOrder.items.forEach((item) => lines.set(cuttingLineKey(item), item));
    }

    return lines;
  }, [savedOrder, partyChanged]);

  // What each saved line had and received, even once the party changed: the server lets a line keep that many pieces.
  const savedLineByKey = useMemo(
    () =>
      new Map(
        (savedOrder?.items || []).map((item) => [cuttingLineKey(item), item])
      ),
    [savedOrder]
  );

  // The picker's cards: one per item with cut pieces ready that the party can be asked to turn, with its sizes, and the
  // order's own lines it no longer offers, in their item's card or a card of their own. A size the order has shows what
  // its line copied.
  const cards = useMemo((): TurningCardProps[] => {
    const byItem = new Map<number, TurningCardProps>();
    const sizeOf = (
      key: string,
      size_id: number | null,
      sizeName: string | null,
      offered: TurningOptionSizeProps | undefined
    ): TurningCardSize => {
      const saved = savedByKey.get(key);
      const line = savedLineByKey.get(key);

      return {
        key,
        size_id,
        sizeName,
        cutLength: saved ? saved.cut_length : offered?.cut_length ?? null,
        rate: saved ? saved.rate : offered?.rate,
        rateSource: saved ? saved.rate_source : offered?.rate_source,
        pending: offered?.pending_qty ?? 0,
        ready: partyInfo.ready[key] ?? offered?.ready_qty ?? 0,
        savedQty: line?.qty ?? 0,
        received: saved?.received_qty || 0,
        closed: !!saved?.closed,
      };
    };

    partyInfo.options.forEach((option) =>
      byItem.set(option.item_id, {
        key: String(option.item_id),
        item_id: option.item_id,
        categoryId: option.category?.id ?? 0,
        code: option.code,
        subtitle: option.parent
          ? `મુખ્ય આઈટમ: ${option.parent.name}`
          : option.description,
        editItemId: option.parent?.id ?? option.item_id,
        imageUrl: option.image_url,
        materialName: option.material?.name ?? null,
        sizes: option.sizes.map((size) =>
          sizeOf(
            optionKey(option.item_id, size.size_id),
            size.size_id,
            size.size_name,
            size
          )
        ),
      })
    );

    savedByKey.forEach((saved, key) => {
      if (optionByKey.has(key)) return;

      const card = byItem.get(saved.item_id) || {
        key: String(saved.item_id),
        item_id: saved.item_id,
        categoryId: saved.item?.category_id ?? 0,
        code: saved.item?.name || `#${saved.item_id}`,
        subtitle: saved.item?.parent
          ? `મુખ્ય આઈટમ: ${saved.item.parent.name}`
          : saved.item?.description ?? null,
        editItemId: saved.item?.parent?.id ?? saved.item_id,
        imageUrl: saved.item?.image_url ?? null,
        materialName: saved.material?.name ?? null,
        sizes: [],
      };

      card.sizes.push(
        sizeOf(key, saved.size_id, saved.size?.name ?? null, undefined)
      );
      byItem.set(saved.item_id, card);
    });

    return Array.from(byItem.values());
  }, [partyInfo, savedByKey, savedLineByKey, optionByKey]);

  // What each line shows: a saved line what it copied, unless it asks to copy again; any other line the party's offer.
  const views = useMemo(
    () =>
      items.map((line: ITurningLineInput): TurningLineView => {
        const key = cuttingLineKey(line);
        const saved = savedByKey.get(key);
        const entry = optionByKey.get(key);
        const keep = !!saved && !line.refresh;
        const received = saved?.received_qty || 0;
        const closed = !!saved?.closed;
        const qty = Number(line.qty) || 0;

        const cutLength = keep
          ? saved!.cut_length
          : entry?.size.cut_length ?? null;

        // The rate the line would copy: a saved line's own, else the party's offer. A user who may see rates types the
        // line's rate over it; another rate than that one is the order's own (3).
        const defaultRate = keep ? saved!.rate : entry?.size.rate;
        const defaultSource = keep
          ? saved!.rate_source
          : entry?.size.rate_source;
        const rate = withRates ? rateNumber(line.rate) ?? undefined : undefined;
        const typedOver =
          rate !== undefined &&
          defaultRate !== undefined &&
          Math.abs(rate - defaultRate) >= 0.005;
        const rateHint =
          !withRates || defaultRate === undefined
            ? null
            : typedOver
            ? `બદલેલ ભાવ • ${
                defaultSource === 3 ? "પહેલા" : rateSourceLabel(defaultSource)
              } ₹${formatAmount(defaultRate)}`
            : rate === undefined
            ? `${rateSourceLabel(defaultSource)} ₹${formatAmount(defaultRate)}`
            : rateSourceLabel(defaultSource);

        // The cut pieces the line may take: those ready (the party's details give them for every key with pieces; for
        // an order being edited with its own lines counted as ready), or what it already had or received.
        const ready = partyInfoLoading ? null : partyInfo.ready[key] ?? 0;
        const cap =
          ready === null
            ? null
            : turningCap(ready, savedLineByKey.get(key)?.qty ?? 0, received);
        const over = cap !== null && qty > cap;

        // An in-house party is offered every item with cut pieces ready, so one it isn't offered has changed on the
        // item page, unless no pieces are ready.
        const problem = partyInfoLoading
          ? null
          : !keep && !entry
          ? (cap ?? 0) <= 0
            ? "આ આઈટમ કટીંગ તૈયાર નથી, તેને કાઢી નાખો"
            : partyInfo.inhouse
            ? "આ આઈટમ બદલાઈ છે, તેને કાઢી નાખો"
            : "આ પાર્ટી માટે આ આઈટમ નો ટર્નિંગ ભાવ નથી અથવા આઈટમ બદલાઈ છે, તેને કાઢી નાખો"
          : over
          ? `કટીંગ તૈયાર ફક્ત ${formatQty(cap)} નંગ છે`
          : null;

        // A saved line that nothing has been received of can copy the item page's new values again.
        const changes: string[] = [];

        if (saved && entry && !line.refresh && received === 0 && !closed) {
          if ((entry.size.cut_length ?? null) !== (saved.cut_length ?? null)) {
            changes.push(
              `લંબાઈ ${lengthText(saved.cut_length, sizeUnit)} → ${lengthText(
                entry.size.cut_length,
                sizeUnit
              )}`
            );
          }

          if ((entry.option.material?.id ?? null) !== saved.material_id) {
            changes.push(
              `મટીરીયલ ${saved.material?.name || "-"} → ${
                entry.option.material?.name || "-"
              }`
            );
          }

          // A rate typed on the order was meant to differ from the item and party pages.
          if (
            withRates &&
            saved.rate_source !== 3 &&
            entry.size.rate !== undefined &&
            saved.rate !== undefined &&
            entry.size.rate !== saved.rate
          ) {
            changes.push(
              `ભાવ ₹${formatAmount(saved.rate)} → ₹${formatAmount(
                entry.size.rate
              )}`
            );
          }
        }

        return {
          key,
          categoryId:
            entry?.option.category?.id ??
            savedCategoryOfItem.get(line.item_id) ??
            0,
          code: entry?.option.code ?? saved?.item?.name ?? `#${line.item_id}`,
          sizeName: entry?.size.size_name ?? saved?.size?.name ?? null,
          imageUrl: entry?.option.image_url ?? saved?.item?.image_url ?? null,
          materialName: keep
            ? saved!.material?.name ?? null
            : entry?.option.material?.name ?? null,
          cutLength,
          rate,
          rateSource: typedOver ? 3 : defaultSource,
          rateHint,
          received,
          closed,
          ready,
          over,
          problem,
          changes,
        };
      }),
    [
      items,
      savedByKey,
      savedLineByKey,
      savedCategoryOfItem,
      optionByKey,
      partyInfo,
      partyInfoLoading,
      withRates,
      sizeUnit,
    ]
  );

  // A new order starts with one empty box; an order being edited with a box per category of its lines, in line order.
  const lineCategoryIds = useMemo(
    () => views.map((view) => view.categoryId),
    [views]
  );
  const { boxes, addBox, removeBox, changeBoxCategory, boxProblem } =
    useCategoryBoxes(
      () =>
        (defaultValues?.items || []).map(
          (line) => savedCategoryOfItem.get(line.item_id) ?? 0
        ),
      lineCategoryIds,
      remove
    );

  // Every category the form can name: the party's (only those it has something to turn in) and those of the saved lines.
  const categoryById = useMemo(() => {
    const categories = new Map<number, CuttingCategoryProps>();

    partyInfo.options.forEach((option) => {
      if (option.category) categories.set(option.category.id, option.category);
    });

    (savedOrder?.items || []).forEach((item) => {
      if (item.item?.category && !categories.has(item.item.category.id)) {
        categories.set(item.item.category.id, item.item.category);
      }
    });

    return categories;
  }, [partyInfo, savedOrder]);

  // The select offers the categories the party has something to turn in.
  const categoryOptions = useMemo((): CuttingCategoryOption[] => {
    const offered = new Map<number, CuttingCategoryProps>();

    partyInfo.options.forEach((option) => {
      if (option.category) offered.set(option.category.id, option.category);
    });

    return Array.from(offered.values())
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((category) => ({ value: category.id, label: categoryText(category) }));
  }, [partyInfo]);

  const categoryLabel = (categoryId: number | null) => {
    const category =
      categoryId !== null ? categoryById.get(categoryId) : undefined;

    return category ? categoryText(category) : null;
  };

  const handlePickerClose = (lines: ITurningLineInput[]) => {
    setPickerCategoryId(null);

    const current = getValues("items");
    const same =
      lines.length === current.length &&
      lines.every(
        (line, index) =>
          cuttingLineKey(line) === cuttingLineKey(current[index]) &&
          line.qty === current[index].qty &&
          line.rate === current[index].rate &&
          line.refresh === current[index].refresh
      );

    if (!same) {
      replace(lines);
      clearErrors("items");
    }
  };

  // For a user who may see rates, copying the item again puts the party's current rate in the rate box, and undoing it
  // the saved one.
  const handleRefresh = (index: number, refresh: boolean) => {
    setValue(`items.${index}.refresh`, refresh, { shouldDirty: true });

    if (withRates) {
      const key = cuttingLineKey(getValues(`items.${index}`));
      const rate = refresh
        ? optionByKey.get(key)?.size.rate
        : savedByKey.get(key)?.rate;

      setValue(`items.${index}.rate`, rateText(rate), { shouldDirty: true });
    }
  };

  const canPick = !!partyId && !partyInfoLoading;
  const pickerLabel = !partyId
    ? "પહેલા પાર્ટી પસંદ કરો"
    : partyInfoLoading
    ? "આઈટમ લોડ થઈ રહી છે..."
    : "આઈટમ પસંદ કરો";

  const handleOnSubmit: SubmitHandler<ITurningOrderFormInput> = (data) => {
    // Every box needs a category and an item of it; there is always a box, so an order without lines stops here.
    const boxError = boxProblem();

    if (boxError) {
      toast.error(boxError, { ...(toastStyles as any) });
      return;
    }

    // The lines are checked against the cut pieces ready, which have to be loaded.
    if (partyInfoLoading) {
      toast.error("પાર્ટી ની વિગતો લોડ થઈ રહી છે, થોડી વાર પછી સાચવો", {
        ...(toastStyles as any),
      });
      return;
    }

    const problem = views.find((view) => view.problem);

    if (problem) {
      toast.error(`${problem.code}: ${problem.problem}`, {
        ...(toastStyles as any),
      });
      return;
    }

    onSubmit(data);
  };

  return (
    <div className={formCardClass}>
      <div className={cx(cardContentClass, "pt-6")}>
        <form
          onSubmit={(event) => {
            setShowBoxErrors(true);
            handleSubmit(handleOnSubmit)(event);
          }}
          className="space-y-6"
        >
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <div className="space-y-2 md:col-span-2">
              <label htmlFor="turning-order-party" className={fieldLabelClass}>
                પાર્ટી પસંદ કરો <span className="text-red-500">*</span>
              </label>
              <div className={cx("relative", errors.party_id && "error")}>
                <LuBuilding2 className={formSelectIconClass} />
                <Controller
                  control={control}
                  name="party_id"
                  rules={{ required: true, min: 1 }}
                  render={({ field }) => (
                    <Select
                      inputId="turning-order-party"
                      ref={field.ref}
                      options={partyOptions}
                      value={
                        partyOptions.find(
                          (option) => option.value === field.value
                        ) || null
                      }
                      onChange={(option: any) => {
                        const value = option?.value || 0;

                        if (value === field.value) return;

                        field.onChange(value);
                        onPartyChange(value);
                      }}
                      isDisabled={partyLocked}
                      placeholder="પાર્ટી સિલેક્ટ કરો..."
                      noOptionsMessage={() => "ટર્નિંગ ની પાર્ટી મળી નથી"}
                      styles={iconSelectStyles}
                      isSearchable
                      className={cx(
                        "react-select-container",
                        errors.party_id && "error"
                      )}
                      classNamePrefix="react-select"
                    />
                  )}
                />
              </div>
              {errors.party_id ? (
                <p className={fieldErrorTextClass}>
                  <LuAlertCircle className="h-4 w-4 mr-1" />
                  પાર્ટી સિલેક્ટ કરો
                </p>
              ) : partyLocked ? (
                <p className="text-xs text-gray-500">
                  આ ઓર્ડર ની રિસીવ થઈ ગઈ હોવાથી પાર્ટી બદલી શકાશે નહીં
                </p>
              ) : (
                partyChanged && (
                  <p className="text-xs text-orange-700">
                    બીજી પાર્ટી પસંદ કરતાં દરેક આઈટમ ની લંબાઈ અને ભાવ નવી પાર્ટી મુજબ લાગશે
                  </p>
                )
              )}
            </div>

            <div className="space-y-2">
              <label htmlFor="turning-order-date" className={fieldLabelClass}>
                તારીખ અને સમય <span className="text-red-500">*</span>
              </label>
              <div className={cx("relative", errors.date && "error")}>
                <LuDatabase className={formSelectIconClass} />
                <Input
                  id="turning-order-date"
                  type="datetime-local"
                  className={fieldInputClass(!!errors.date)}
                  {...register("date", { required: true })}
                />
                {errors.date && (
                  <LuAlertCircle className={fieldErrorIconClass} />
                )}
              </div>
              {errors.date && (
                <p className={fieldErrorTextClass}>
                  <LuAlertCircle className="h-4 w-4 mr-1" />
                  તારીખ અને સમય દાખલ કરો
                </p>
              )}
            </div>
          </div>

          <TurningOrderLines
            control={control}
            fields={fields}
            errors={errors}
            views={views}
            boxes={boxes}
            categoryOptions={categoryOptions}
            categoryLabel={categoryLabel}
            categoriesMessage={
              !partyId
                ? "પહેલા પાર્ટી પસંદ કરો"
                : partyInfoLoading
                ? "કેટેગરી લોડ થઈ રહી છે..."
                : "આ પાર્ટી માટે ટર્નિંગ ની કોઈ કટીંગ તૈયાર આઈટમ નથી"
            }
            showBoxErrors={showBoxErrors}
            onAddBox={addBox}
            onRemoveBox={removeBox}
            onChangeBoxCategory={changeBoxCategory}
            onRemove={remove}
            onOpenPicker={setPickerCategoryId}
            onRefresh={handleRefresh}
            refreshed={items.map((item) => !!item.refresh)}
            canPick={canPick}
            pickerLabel={pickerLabel}
            withRates={withRates}
            sizeUnit={sizeUnit}
          />

          <div className="flex flex-col md:flex-row md:items-center md:justify-end gap-4 pt-4">
            <div className="flex justify-end space-x-4">
              <Button
                type="button"
                variant="outline"
                onClick={onCancel}
                disabled={isSubmitting}
              >
                રદ કરો
              </Button>

              <button
                type="submit"
                className={gradientSubmitButtonClass}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <div className="flex items-center">
                    <div className={buttonSpinnerClass}></div>
                    સાચવી રહ્યા છીએ...
                  </div>
                ) : (
                  submitLabel
                )}
              </button>
            </div>
          </div>
        </form>
      </div>

      {pickerCategoryId !== null && (
        <TurningItemPickerModal
          cards={cards.filter((card) => card.categoryId === pickerCategoryId)}
          cardsLoading={partyInfoLoading}
          categoryLabel={categoryLabel(pickerCategoryId) ?? undefined}
          initialLines={getValues("items")}
          withRates={withRates}
          inhouse={partyInfo.inhouse}
          sizeUnit={sizeUnit}
          onClose={handlePickerClose}
        />
      )}
    </div>
  );
};

export default TurningOrderForm;
